import { c } from 'ttag';

import { CatalogueEmptyReason } from '../../../graphql/__generated__/schema';
import { refusalCode } from '../../../graphql/refusalCode';

/**
 * Why a catalogue of the onboarding journey has nothing to offer. Three empties from two channels:
 * emptyReason (EMPTY_CATALOGUE, NO_ACCOUNT_IN_PERIMETER) and a GraphQL error code (a refusal, not an
 * empty list). An empty catalogue is the ordinary state, not a failure.
 */
export interface CatalogueEmptiness {
    title: string;
    /** What to do, or nothing. Never "retry" unless retrying is the fix. */
    guidance?: string;
    /** How loudly: info for an empty catalogue, warning for a refusal. */
    tone: 'info' | 'warning' | 'danger';
    /** The server's own sentence, kept only when the refusal is one we cannot read. */
    detail?: string;
}

// Shared wording; the missing thing's name is passed in WHOLE, not interpolated, because French
// agrees the participle with its subject.
const refusalEmptiness = (error: unknown, unreadableTitle: string): CatalogueEmptiness => {
    switch (refusalCode(error)) {
        case 'MISSING_PERMISSION':
            return {
                title: c('Onboarding catalogue').t`Your role does not let you manage users.`,
                guidance: c('Onboarding catalogue')
                    .t`So this list was refused rather than answered: it is not empty, it was not read. Ask an administrator for the user-management permission. The rest of the journey will refuse the same way when the invitation is sent.`,
                tone: 'warning',
            };
        case 'FORBIDDEN_OUT_OF_SCOPE':
            return {
                title: c('Onboarding catalogue').t`One of the selected accounts is outside your portfolio.`,
                guidance: c('Onboarding catalogue')
                    .t`The accounts were refused as a block rather than quietly narrowed, so nothing is offered here. Go back to the account step and drop the account you cannot act on.`,
                tone: 'danger',
            };
        case 'NOT_AUTHENTICATED':
            return {
                title: c('Onboarding catalogue').t`Your session no longer carries an active membership.`,
                guidance: c('Onboarding catalogue')
                    .t`Sign in again. The draft entered so far lives in this page and will not survive it, so copy the email address before you do.`,
                tone: 'danger',
            };
        default:
            return {
                title: unreadableTitle,
                guidance: c('Onboarding catalogue')
                    .t`Nothing is offered below for want of an answer, not for want of entries. Both steps are optional: you can retry, or continue without attributing anything.`,
                tone: 'danger',
                detail: error instanceof Error ? error.message : String(error),
            };
    }
};

// error wins over emptyReason: a refused query carries no reason and must not read as an empty catalogue.
export const dashboardEmptiness = (
    error: unknown,
    emptyReason: CatalogueEmptyReason | null | undefined
): CatalogueEmptiness | null => {
    if (error) {
        return refusalEmptiness(error, c('Onboarding catalogue').t`The dashboards could not be loaded.`);
    }
    switch (emptyReason) {
        case CatalogueEmptyReason.EmptyCatalogue:
            return {
                title: c('Onboarding catalogue').t`No dashboard is declared on the selected accounts.`,
                tone: 'info',
            };
        case CatalogueEmptyReason.NoAccountInPerimeter:
            return {
                title: c('Onboarding catalogue').t`You carry no account, so no dashboard can hang from one.`,
                guidance: c('Onboarding catalogue')
                    .t`A dashboard is the object of an account, and your portfolio holds none. Asking for a portfolio is the way out — declaring a dashboard would change nothing here.`,
                tone: 'warning',
            };
        default:
            return null;
    }
};

/** Same reading for the partner lists, whose catalogue hangs from no account at all. */
export const partnerListEmptiness = (
    error: unknown,
    emptyReason: CatalogueEmptyReason | null | undefined
): CatalogueEmptiness | null => {
    if (error) {
        return refusalEmptiness(error, c('Onboarding catalogue').t`The partner lists could not be loaded.`);
    }
    switch (emptyReason) {
        case CatalogueEmptyReason.EmptyCatalogue:
            return {
                title: c('Onboarding catalogue').t`No partner list exists yet.`,
                guidance: c('Onboarding catalogue')
                    .t`Lists are a platform referential curated in the back-office, and none has been declared so far. Nothing is broken and there is nothing to retry. This step is optional — continue, or declare one from the « Listes de partenaires » screen, then come back.`,
                tone: 'info',
            };
        case CatalogueEmptyReason.NoAccountInPerimeter:
            // Unreachable by design (no account axis), but rendered rather than assumed away, so a
            // future API answer would not be a blank block.
            return {
                title: c('Onboarding catalogue').t`The partner lists came back bounded by a perimeter.`,
                guidance: c('Onboarding catalogue')
                    .t`This catalogue is global and has no account axis, so this answer is not one the screen expects. Report it: the journey continues without a list.`,
                tone: 'warning',
            };
        default:
            return null;
    }
};
